import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  Text,
  Image,
  HStack,
  IconButton,
  SimpleGrid,
  Skeleton,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { teamMembers, teamPhoto } from '../lib/storage';
import { theme } from '../lib/theme';

const Team: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState<Record<string, boolean>>({});
  const sectionRef = useRef<HTMLDivElement>(null);

  const itemsPerPage = 4;
  const totalPages = Math.ceil(teamMembers.length / itemsPerPage);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    const section = sectionRef.current;
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % totalPages);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const handleImageLoad = (id: string) => {
    setImagesLoaded((prev) => ({ ...prev, [id]: true }));
  };

  const getCurrentMembers = () => {
    const start = currentIndex * itemsPerPage;
    const end = start + itemsPerPage;
    return teamMembers.slice(start, end);
  };

  return (
    <Box id="team" ref={sectionRef} bg="white" py={{ base: 16, md: 24 }} position="relative">
      <Container maxW="1400px">
        <VStack spacing={12} align="stretch">
          <VStack
            spacing={4}
            textAlign="center"
            opacity={isVisible ? 1 : 0}
            transform={isVisible ? 'translateY(0)' : 'translateY(40px)'}
            transition="all 0.8s ease-out"
          >
            <Heading as="h2" fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }} color="yaleBlue" fontWeight={700}>
              Conheça a equipe da Mendes Assessoria Jurídica
            </Heading>
            <Box w="80px" h="4px" bg="stormyTeal" borderRadius="2px" />
          </VStack>

          <Box
            opacity={isVisible ? 1 : 0}
            transform={isVisible ? 'translateY(0)' : 'translateY(40px)'}
            transition="all 0.8s ease-out 0.2s"
          >
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={{ base: 6, md: 8 }} mb={8}>
              {getCurrentMembers().map((member, index) => (
                <VStack
                  key={member.id}
                  spacing={4}
                  align="stretch"
                  opacity={isVisible ? 1 : 0}
                  transform={isVisible ? 'translateY(0)' : 'translateY(40px)'}
                  transition="all 0.6s ease-out"
                  style={{ transitionDelay: `${0.3 + index * 0.1}s` }}
                >
                  <Box
                    position="relative"
                    w="full"
                    h={{ base: '350px', md: '400px' }}
                    overflow="hidden"
                    borderRadius="8px"
                    boxShadow="0 4px 12px rgba(0,0,0,0.1)"
                  >
                    <Skeleton isLoaded={imagesLoaded[member.id]} w="full" h="full">
                      <Image
                        src={member.image}
                        alt={`${member.name} - ${member.role} da Mendes Assessoria Jurídica`}
                        objectFit="cover"
                        w="full"
                        h="full"
                        loading="lazy"
                        onLoad={() => handleImageLoad(member.id)}
                        transition="transform 0.5s ease"
                        _hover={{ transform: 'scale(1.05)' }}
                      />
                    </Skeleton>
                  </Box>

                  <VStack align="flex-start" spacing={2} px={2}>
                    <Heading as="h3" fontSize={{ base: 'lg', md: 'xl' }} color="yaleBlue" fontWeight={700} lineHeight="1.3">
                      {member.name}
                    </Heading>
                    <Text fontSize={{ base: 'sm', md: 'md' }} color="stormyTeal" fontWeight={600}>
                      {member.role}
                    </Text>
                    {member.oab && (
                      <Text fontFamily={theme.fonts.team} fontSize={{ base: 'xs', md: 'sm' }} color="graphite" lineHeight="1.5">
                        {member.oab}
                      </Text>
                    )}
                  </VStack>
                </VStack>
              ))}
            </SimpleGrid>

            <HStack justify="center" spacing={4}>
              <IconButton
                aria-label="Equipe anterior"
                icon={<ChevronLeftIcon boxSize={8} />}
                onClick={handlePrev}
                isDisabled={currentIndex === 0}
                size="lg"
                bg="yaleBlue"
                color="white"
                _hover={{ bg: 'stormyTeal', transform: 'translateY(-2px)' }}
                _disabled={{
                  bg: 'alabasterGrey',
                  color: 'graphite',
                  cursor: 'not-allowed',
                  _hover: { bg: 'alabasterGrey', transform: 'none' },
                }}
                transition="all 0.3s ease"
              />

              <HStack spacing={2}>
                {Array.from({ length: totalPages }).map((_, index) => (
                  <Box
                    key={index}
                    w={currentIndex === index ? '32px' : '8px'}
                    h="8px"
                    bg={currentIndex === index ? 'yaleBlue' : 'alabasterGrey'}
                    borderRadius="4px"
                    transition="all 0.3s ease"
                    cursor="pointer"
                    onClick={() => setCurrentIndex(index)}
                  />
                ))}
              </HStack>

              <IconButton
                aria-label="Próxima equipe"
                icon={<ChevronRightIcon boxSize={8} />}
                onClick={handleNext}
                isDisabled={currentIndex === totalPages - 1}
                size="lg"
                bg="yaleBlue"
                color="white"
                _hover={{ bg: 'stormyTeal', transform: 'translateY(-2px)' }}
                _disabled={{
                  bg: 'alabasterGrey',
                  color: 'graphite',
                  cursor: 'not-allowed',
                  _hover: { bg: 'alabasterGrey', transform: 'none' },
                }}
                transition="all 0.3s ease"
              />
            </HStack>
          </Box>

          <Box
            mt={12}
            opacity={isVisible ? 1 : 0}
            transform={isVisible ? 'translateY(0)' : 'translateY(40px)'}
            transition="all 0.8s ease-out 0.4s"
          >
            <VStack spacing={6}>
              <Heading as="h3" fontSize={{ base: '2xl', md: '3xl' }} color="yaleBlue" fontWeight={700} textAlign="center">
                Nossa equipe completa
              </Heading>
              <Skeleton isLoaded={imagesLoaded['team-photo']} w="full">
                <Image
                  src={teamPhoto}
                  alt="Foto completa da equipe Mendes Assessoria Jurídica reunida"
                  borderRadius="8px"
                  objectFit="cover"
                  m="0 auto"
                  maxW="60%"
                  boxShadow="0 12px 40px rgba(0,0,0,0.15)"
                  loading="lazy"
                  onLoad={() => handleImageLoad('team-photo')}
                />
              </Skeleton>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default Team;
